import { create } from "zustand";

export interface OrgUser {
  user_id: string;
  clerk_user_id: string;
  name: string;
  email: string;
  imageUrl: string;
}

interface UserSettingsState {
  userInfo: any;
  organizationUsers: OrgUser[];
  isLoading: boolean;
  error: string | null;
  fetchUserInfo: (
    organizationId: string,
    userId: string,
    token: string
  ) => Promise<any>;
  fetchUserWritingStyle: (
    organizationId: string,
    userId: string,
    token: string
  ) => Promise<{
    user_writing_style: string;
    enforce_writing_style: boolean;
  } | null>;
  fetchOrganizationUsers: (
    organizationId: string,
    token: string
  ) => Promise<OrgUser[]>;
  saveUserSetting: (
    userId: string,
    organizationId: string,
    field: string,
    value: string | boolean,
    token: string
  ) => Promise<boolean>;
  saveUserSettingFor: (
    targetUserId: string,
    organizationId: string,
    field: string,
    value: string | boolean,
    token: string,
    options?: { mirrorToGlobal?: boolean }
  ) => Promise<boolean>;
  reset: () => void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const useUserSettingsStore = create<UserSettingsState>((set) => ({
  userInfo: null,
  organizationUsers: [],
  isLoading: false,
  error: null,

  fetchUserInfo: async (
    organizationId: string,
    userId: string,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          organization_id: organizationId,
        }),
      });

      if (!response.ok) throw new Error("Failed to fetch user settings");

      const data = await response.json();
      set({
        userInfo: {
          user_writing_style: data.data.user_writing_style,
          enforce_writing_style: data.data.enforce_writing_style,
          enable_gmail_labeling: data.data.enable_gmail_labeling,
        },
        isLoading: false,
      });
      return data;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return null;
    }
  },

  fetchUserWritingStyle: async (
    organizationId: string,
    userId: string,
    token: string
  ) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          organization_id: organizationId,
        }),
      });

      if (!response.ok) throw new Error("Failed to fetch user writing style");

      const data = await response.json();
      return {
        user_writing_style: data.data.user_writing_style,
        enforce_writing_style: data.data.enforce_writing_style,
      };
    } catch (error) {
      console.error("Error fetching user writing style:", error);
      return null;
    }
  },

  fetchOrganizationUsers: async (organizationId: string, token: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/settings/get-organization-users`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            organization_id: organizationId,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch organization users");

      const data = await response.json();
      const organizationUsers: OrgUser[] = data.data || [];
      set({ organizationUsers });
      return organizationUsers;
    } catch (error) {
      console.error("Error fetching organization users:", error);
      set({ organizationUsers: [] });
      return [];
    }
  },

  saveUserSetting: async (
    userId: string,
    organizationId: string,
    field: string,
    value: string | boolean,
    token: string
  ) => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          user_id: userId,
          field,
          value,
        }),
      });

      if (!response.ok) throw new Error("Failed to save user setting");

      const data = await response.json();

      if (field === "user_writing_style") {
        set((state) => ({
          userInfo: { ...state.userInfo, user_writing_style: value },
          isLoading: false,
        }));
      } else if (field === "enforce_writing_style") {
        set((state) => ({
          userInfo: { ...state.userInfo, enforce_writing_style: value },
          isLoading: false,
        }));
      } else if (field === "enable_gmail_labeling") {
        set((state) => ({
          userInfo: { ...state.userInfo, enable_gmail_labeling: value },
          isLoading: false,
        }));
      }

      return data.success;
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      return false;
    }
  },

  saveUserSettingFor: async (
    targetUserId: string,
    organizationId: string,
    field: string,
    value: string | boolean,
    token: string,
    options?: { mirrorToGlobal?: boolean }
  ) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/settings/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          user_id: targetUserId,
          field,
          value,
        }),
      });

      if (!response.ok) throw new Error("Failed to save user setting");

      const data = await response.json();

      // Only mirror into the global (logged-in user) userInfo cache when we are
      // saving the logged-in user's own settings — never when an admin edits
      // another member's writing style.
      if (options?.mirrorToGlobal) {
        set((state) => ({
          userInfo: { ...state.userInfo, [field]: value },
        }));
      }

      return data.success;
    } catch (error) {
      console.error("Error saving user setting:", error);
      return false;
    }
  },

  reset: () =>
    set({ userInfo: null, organizationUsers: [], isLoading: false, error: null }),
}));
